"use client";

export interface MahadashaPeriod {
  planet: string;
  planetHi?: string;
  years: number;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface DashaVisualTimelineProps {
  periods: MahadashaPeriod[];
  currentPlanet?: string;
  locale?: "en" | "hi";
}

export default function DashaVisualTimeline({
  periods,
  currentPlanet,
  locale = "en",
}: DashaVisualTimelineProps) {
  const isHi = locale === "hi";

  return (
    <div className="card-sacred p-4 sm:p-6 my-6" data-design-owner="dasha-visual-timeline">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-serif text-body font-bold text-text-primary">
          {isHi
            ? "120-वर्षीय विंशोत्तरी महादशा कालक्रम"
            : "120-Year Vimshottari Mahadasha Timeline"}
        </h3>
        {currentPlanet && (
          <span className="rounded-full bg-intent-brand px-2.5 py-0.5 text-caption font-semibold text-text-on-brand animate-pulse">
            {isHi ? `सक्रिय: ${currentPlanet}` : `Active: ${currentPlanet}`}
          </span>
        )}
      </div>

      <p className="text-caption text-text-secondary mb-4 leading-relaxed">
        {isHi
          ? "प्रत्येक महादशा का कालखंड विंशोत्तरी चक्र के अनुपात में दर्शाया गया है:"
          : "Each planetary period is proportionally mapped along your 120-year lifetime sequence:"}
      </p>

      {/* Proportional Grid Timeline Bar */}
      <div className="flex h-10 w-full overflow-hidden rounded-xl border border-border-default bg-surface-warm shadow-inner">
        {periods.map((p) => {
          const isCurrent = p.planet === currentPlanet || p.isActive;

          return (
            <div
              key={p.planet}
              className={`flex-1 flex items-center justify-center border-r border-border-default transition-all hover:bg-surface-raised ${
                isCurrent
                  ? "bg-surface-brand-strong text-text-on-brand font-bold z-10 shadow-xs"
                  : "bg-surface-raised text-text-primary"
              }`}
              title={`${p.planet} Mahadasha (${p.years} yrs)`}
            >
              <span className="text-caption font-semibold truncate px-0.5">
                {p.planet.slice(0, 3)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend / Breakdown Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4 text-center">
        {periods.map((p) => {
          const isCurrent = p.planet === currentPlanet || p.isActive;
          return (
            <div
              key={p.planet}
              className={`rounded-lg border p-2 text-caption transition ${
                isCurrent
                  ? "border-border-interactive bg-surface-brand-subtle text-text-primary font-bold shadow-xs"
                  : "border-border-default bg-surface-raised text-text-secondary"
              }`}
            >
              <div className="font-semibold text-text-primary">{p.planet}</div>
              <div className="text-caption text-text-muted">
                {p.years} {isHi ? "वर्ष" : "yrs"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
